import { useEffect, useRef, useState } from 'react';

import { useAuthInfo } from '@propelauth/react';
import { ErrorCode } from 'react-dropzone';
import { toast as sonnerToast } from 'sonner';

import { FileWithPreview, createFilePreview } from './utils';
import { APIBaseChronos } from '../../../api/hosts';
import { BroadcastChannelNames, RelativityAuthMessageTypes, RelativityAuthMessages } from '@/types/broadcast-channels';

interface UseFileUploadLogicProps {
  maxFiles?: number;
}

// For now we consider a upload to the matter to be a upload to the 'All Documents' subset of the
// matter in order to make the migration simpler. We'll likely remove the case <-> file relation
// in the future however.
export const useFileUploadLogic = ({ maxFiles }: UseFileUploadLogicProps) => {
  // File state
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [localError, setLocalError] = useState<ErrorCode | null>(null);

  // Modal state
  const [isRelativityModalOpen, setIsRelativityModalOpen] = useState(false);

  // Relativity auth state
  const authInfo = useAuthInfo();
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const sessionKeyRef = useRef<string | null>(null);

  // API hooks
  // Relativity broadcast channel setup
  useEffect(() => {
    const channel = new BroadcastChannel(BroadcastChannelNames.RelativityAuth);
    broadcastChannelRef.current = channel;

    const handleBroadcastMessage = (event: MessageEvent<RelativityAuthMessages>) => {
      if (!sessionKeyRef.current || event.data.sessionKey !== sessionKeyRef.current) {
        return;
      }

      sessionKeyRef.current = null;

      if (event.data.type === RelativityAuthMessageTypes.Success) {
        setIsRelativityModalOpen(true);
      } else if (event.data.type === RelativityAuthMessageTypes.Error) {
        sonnerToast.error(`Failed to connect to Relativity: ${event.data.description || event.data.error}`);
      }
    };

    channel.addEventListener('message', handleBroadcastMessage);

    return () => {
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
        broadcastChannelRef.current = null;
      }
    };
  }, []);

  // File handling
  const handleFileAccepted = (acceptedFiles: File[], onDrop: (files: File[]) => void) => {
    setLocalError(null);

    if (maxFiles === files.length) {
      return setLocalError(ErrorCode.TooManyFiles);
    }

    const newFiles = acceptedFiles.map(createFilePreview);
    setFiles([...files, ...newFiles]);
    onDrop(acceptedFiles);
  };

  const onDeleteFile = (index: number) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
  };

  // Relativity integration
  const connectRelativity = async () => {
    try {
      const statusResponse = await fetch(`${APIBaseChronos}/oauth/relativity/status`, {
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check Relativity auth status');
      }

      const statusData = await statusResponse.json();
      if (statusData.isAuthenticated === true) {
        setIsRelativityModalOpen(true);
        return;
      }

      const response = await fetch(`${APIBaseChronos}/oauth/relativity/start`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${await authInfo.tokens.getAccessToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to start OAuth flow');
      }

      const data = await response.json();
      const url = new URL(data.authUrl);
      const sessionKey = url.searchParams.get('state');

      if (sessionKey) {
        sessionKeyRef.current = sessionKey;
      }

      window.open(data.authUrl, 'relativity-auth', 'width=800,height=600');
    } catch (error) {
      console.error('Error starting Relativity OAuth:', error);
      sonnerToast.error('Failed to start Relativity integration');
    }
  };

  return {
    // File state
    files,
    localError,
    setLocalError,

    // File handlers
    handleFileAccepted,
    onDeleteFile,

    // Modal state
    isRelativityModalOpen,
    setIsRelativityModalOpen,

    // Integration handlers
    connectRelativity,
  };
};
